import { cn } from '@/shared/utils/cn';
import { SeparatorProps } from '@/shared/types/separator';

/**
 * Separator - Componente de separador visual
 *
 * Componente que muestra una línea separadora horizontal o vertical.
 * Útil para dividir secciones de contenido visualmente.
 *
 * @example
 * ```tsx
 * // Separador horizontal
 * <Separator />
 *
 * // Separador vertical
 * <Separator vertical />
 *
 * // Separador sin padding
 * <Separator padding={false} />
 *
 * // Línea horizontal con etiqueta centrada
 * <Separator label="o continúa con" />
 * ```
 *
 * @see {@link SeparatorProps} para la lista completa de props
 */
export const Separator = ({
  className,
  vertical = false,
  padding = true,
  label,
}: SeparatorProps) => {
  const hasLabel = Boolean(label?.trim()) && !vertical;

  if (hasLabel) {
    return (
      <div
        role="separator"
        aria-orientation="horizontal"
        className={cn(
          'ui-imj',
          'ui-flex ui-w-full ui-items-center',
          padding ? 'ui-py-4' : 'ui-py-0',
          className
        )}
      >
        <div
          aria-hidden
          className="ui-box-border ui-min-h-0 ui-min-w-0 ui-flex-1 ui-border-0 ui-border-t ui-border-solid ui-border-default"
        />
        <span
          className={cn(
            'ui-shrink-0 ui-px-8',
            'ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs',
            'ui-text-tertiary'
          )}
        >
          {label}
        </span>
        <div
          aria-hidden
          className="ui-box-border ui-min-h-0 ui-min-w-0 ui-flex-1 ui-border-0 ui-border-t ui-border-solid ui-border-default"
        />
      </div>
    );
  }

  return (
    <div
      role="separator"
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
      className={cn(
        'ui-imj',
        'ui-border-default',
        vertical
          ? cn(
              'ui-box-border ui-max-h-full ui-min-h-7 ui-w-0 ui-border-0 ui-border-r ui-border-solid',
              padding ? 'ui-mx-4' : 'ui-m-0'
            )
          : cn(
              'ui-box-border ui-h-0 ui-w-full ui-border-0 ui-border-t ui-border-solid',
              padding ? 'ui-my-4' : 'ui-m-0'
            ),
        className
      )}
    />
  );
};
